import { useState, type FormEvent } from "react";
import Breadcrumbs from "@/components/feature/Breadcrumbs";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db701psjfl1ikpv8sphg";

export default function About() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") payload.append(key, value);
    });

    setStatus("loading");
    setFormError("");
    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSuccess = response.ok && parsed?.code === "OK";
      if (isSuccess) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && serverMsg.toLowerCase().includes("spam")
            ? "提交未通过校验，请检查内容后重试"
            : serverMsg || "提交失败，请稍后重试",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  const stats = [
    { value: "30+", label: "收录影视作品" },
    { value: "4", label: "精选影视专题" },
    { value: "6", label: "演员资料" },
    { value: "0", label: "提供播放下载" },
  ];

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <title>关于我们 - 善良的嫂子影视站</title>
      <meta
        name="description"
        content="了解善良的嫂子影视站的定位、内容范围与联系方式。本站专注影视内容介绍与解析，不提供播放与下载服务。"
      />
      <meta name="keywords" content="关于我们,善良的嫂子,影视站介绍,联系方式,内容说明" />

      <Breadcrumbs items={[{ label: "关于我们" }]} />

      <div className="mt-4 border-b border-background-200 pb-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">关于我们</h1>
        <p className="mt-2 max-w-3xl text-sm text-foreground-500">
          一个专注影视内容介绍与解析的信息平台
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-focus-3-line"></i>
              </span>
              网站定位
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              本站是一个专业影视内容展示与介绍平台，重点围绕热门影视剧《善良的嫂子》展开，
              系统整理剧情梗概、角色设定、演员资料与剧集看点，同时向同类型、同题材的优质影视作品延伸推荐。
              我们希望通过清晰、完整的内容组织，帮助影视爱好者快速了解一部作品的来龙去脉。
            </p>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-file-list-3-line"></i>
              </span>
              内容范围
            </h2>
            <ul className="mt-3 space-y-2 text-sm leading-relaxed text-foreground-600 md:text-base">
              <li className="flex gap-2">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-500">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                提供影视剧的剧情介绍、详细解析与剧集目录说明。
              </li>
              <li className="flex gap-2">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-500">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                提供演员个人资料、代表作品与角色介绍。
              </li>
              <li className="flex gap-2">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-500">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                提供按类型、年代、地区筛选以及关键词搜索的浏览方式。
              </li>
              <li className="flex gap-2">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-500">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                提供收藏与相关推荐功能，方便整理与发现感兴趣的内容。
              </li>
            </ul>
          </section>

          <section className="rounded-lg border border-primary-200 bg-primary-50 p-4 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-information-line"></i>
              </span>
              使用说明
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              本站为纯内容介绍平台，<strong className="text-primary-700">不提供任何影视资源的播放与下载服务</strong>，
              页面中出现的剧集目录、资源推荐等均为内容介绍与观剧参考用途。
              站内的收藏、筛选、搜索等功能均在浏览器本地完成，不涉及任何账号或数据上传。
              所有影视信息与版权归相关权利人所有，本站仅作介绍与展示。
            </p>
          </section>
        </div>

        <aside className="flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">内容数据一览</h2>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-md bg-background-100 p-3 text-center">
                  <p className="font-heading text-xl font-bold text-primary-600">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">联系方式</h2>
            <ul className="mt-3 space-y-3 text-sm text-foreground-600">
              <li className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-mail-line"></i>
                </span>
                contact@kind-sister-in-law.com
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line"></i>
                </span>
                工作日 09:00 - 18:00
              </li>
            </ul>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">版权声明</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              © 2025 善良的嫂子影视站。站内文字内容仅为影视介绍用途整理，如涉及版权问题，请通过上方邮箱与我们联系。
            </p>
          </section>
        </aside>
      </div>

      <section className="mt-10 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
        <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">意见反馈</h2>
        <p className="mt-1 text-sm text-foreground-500">
          对本站内容有任何建议或发现信息有误，欢迎通过下方表单告诉我们。
        </p>

        {status === "success" ? (
          <div className="mt-5 flex items-center gap-3 rounded-lg bg-primary-50 p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-checkbox-circle-fill text-2xl"></i>
            </span>
            <div>
              <p className="font-heading text-base font-semibold text-foreground-900">反馈提交成功</p>
              <p className="text-sm text-foreground-500">感谢你的建议，我们会认真查看。</p>
            </div>
          </div>
        ) : (
          <form
            id="about-feedback-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2"
          >
            <input
              type="text"
              name="website_alt"
              className="about-extra-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="flex flex-col gap-2">
              <label htmlFor="fb-name" className="text-sm font-medium text-foreground-700">
                姓名
              </label>
              <input
                id="fb-name"
                name="name"
                type="text"
                required
                placeholder="请输入你的称呼"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="fb-email" className="text-sm font-medium text-foreground-700">
                邮箱
              </label>
              <input
                id="fb-email"
                name="email"
                type="email"
                required
                placeholder="用于回复的联系邮箱"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
              />
            </div>

            <div className="flex flex-col gap-2 md:col-span-2">
              <label htmlFor="fb-type" className="text-sm font-medium text-foreground-700">
                反馈类型
              </label>
              <select
                id="fb-type"
                name="type"
                defaultValue="内容建议"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
              >
                <option value="内容建议">内容建议</option>
                <option value="信息纠错">信息纠错</option>
                <option value="影视推荐">影视推荐</option>
                <option value="其他问题">其他问题</option>
              </select>
            </div>

            <div className="flex flex-col gap-2 md:col-span-2">
              <label htmlFor="fb-message" className="text-sm font-medium text-foreground-700">
                反馈内容
              </label>
              <textarea
                id="fb-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请详细描述你的建议或问题（最多 500 字）"
                className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
              />
            </div>

            {status === "error" && (
              <p className="md:col-span-2 rounded-md bg-primary-50 px-3 py-2.5 text-sm text-primary-700">
                {formError}
              </p>
            )}

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={status === "loading"}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <span className="flex h-4 w-4 items-center justify-center"><i className="ri-loader-4-line animate-spin"></i></span>
                    提交中...
                  </>
                ) : (
                  <>
                    <span className="flex h-4 w-4 items-center justify-center"><i className="ri-send-plane-line"></i></span>
                    提交反馈
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}